import { io, Socket } from 'socket.io-client';
import type { DisplayPublic } from './api';
import { API_URL } from './api';
import type { GameEffect } from './effects/types';

export interface ProjectedGameState {
  home: { score: number; fouls: number; timeoutsRemaining: number; bonus: boolean; doubleBonus?: boolean };
  away: { score: number; fouls: number; timeoutsRemaining: number; bonus: boolean; doubleBonus?: boolean };
  period: number;
  clock: {
    m: number;
    s: number;
    running: boolean;
    startedAt?: string | null;
  };
  possession: 'home' | 'away' | null;
  playerStats?: Record<string, Record<string, number>>;
  onCourt: { home: string[]; away: string[] };
  playerStints?: Record<string, string>;
  intermission?: 'halftime' | 'period_break' | null;
  intermissionEndsAt?: string | null;
  sportState?: Record<string, unknown>;
  winProbHome: number;
  scoringRun: { side: 'home' | 'away'; count: number } | null;
}

export interface RosterPlayer {
  id: string;
  name: string;
  jerseyNumber: string | null;
  position: string | null;
}

export interface RosterSide {
  teamId: string;
  name: string;
  abbreviation: string | null;
  colorPrimary: string | null;
  colorSecondary: string | null;
  logoUrl: string | null;
  players: RosterPlayer[];
}

export interface GameSnapshot {
  gameId: string;
  sportSlug: string;
  state: ProjectedGameState;
  rosters: { home: RosterSide; away: RosterSide };
  venueName: string | null;
}

// Backward-compat alias — old code may still reference GameStateMessage.
export type GameStateMessage = GameSnapshot;

/**
 * Connects a Socket.IO client using the display's long-lived token in the
 * auth handshake. The backend gateway joins the display + assigned-game
 * rooms and emits initial state immediately.
 *
 * If API_URL is '' (default — same-origin via the display's nginx) the
 * client connects to the current page origin; nginx proxies /socket.io/*
 * to the backend.
 */
export function connectSocket(
  displayToken: string,
  handlers: {
    onGameState?: (msg: GameStateMessage) => void;
    onGameEffect?: (effect: GameEffect) => void;
    onDisplayConfig?: (msg: { channelType: string; displayConfig: Record<string, unknown> }) => void;
    onDisconnect?: () => void;
    onConnect?: () => void;
  },
): Socket {
  const url = API_URL || window.location.origin;
  const socket = io(url, {
    auth: { displayToken },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 1500,
    reconnectionDelayMax: 8000,
  });

  if (handlers.onConnect) socket.on('connect', handlers.onConnect);
  if (handlers.onDisconnect) socket.on('disconnect', handlers.onDisconnect);
  if (handlers.onGameState) socket.on('game:state', handlers.onGameState);
  if (handlers.onGameEffect) socket.on('game:effect', handlers.onGameEffect);
  if (handlers.onDisplayConfig) socket.on('display:config', handlers.onDisplayConfig);

  return socket;
}

export type { DisplayPublic };
